import { NgxMatDateAdapter } from '@angular-material-components/datetime-picker';
import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { Chart, ChartItem } from 'chart.js';
import { KeyWord } from 'common/constant/KeyWords';
import { MILL_SECONDS } from 'common/constant/general';
import { Decimation } from 'common/utility/decimation/decimation';
import { Logger } from 'common/utility/logger/logger';
import { MB_RESPONSE, MB_TYPE, MessageBoxComponent, MessageBoxOptions } from 'components/message-box/message-box.component';
import { NgxSpinnerService } from 'ngx-spinner';
import { Subject, debounceTime, fromEvent, takeUntil } from 'rxjs';
import { NodeTypes } from 'services/definition/class/node-types';
import { DefinitionService } from 'services/definition/definition.service';
import { SourceDefinitionStatus, TreeNodeType } from 'services/definition/enum';
import { IDeviceNode, IStorageTreeNode, ITelemetryNode, ITreeNode } from 'services/definition/interface';
import { GetDataRequestStatus, QueryRequest, SORT_ORDER } from 'services/get-data/get-data-common';
import { GetDataService } from 'services/get-data/get-data.service';
import { SpinnerTypes } from 'services/loading/spinner/spinner-type';
import { IQueryItem, IQueryListItem, QueryRequestStatus, QueryService } from 'services/query';
import { TranslationService } from 'services/translation/translation.service';
import { ANALYZE_DEFAULT, AnalyzeSettings, FUNC_TYPE, Y_AXIS_POSITION } from './analyze-common';
import { AnalyzeSettingsComponent } from './analyze-settings/analyze-settings.component';
import { QueryNameComponent, RESPONSE } from './query-name/query-name.component';
import { WorkerMessage, WorkerRequest, WorkerResponse, WorkerResult } from './worker/download-worker-interface';

// Registerables
import zoomPlugin from 'chartjs-plugin-zoom';
Chart.register(zoomPlugin)

// https://www.chartjs.org/chartjs-plugin-zoom/samples/basic.html

// Constants
const DEFAULT_TIME_RANGE = 1 * 1 * ((60 * 60) * 1000); // 1 Hour Default, in milliseconds
const DOWNLOAD_FILENAME = "Data-Export-"

// Loading timeout
const MAX_TIMEOUT = 60;
const QUERY_VERSION = 1

// Interfaces
interface MenuItems {
  id: any;
  label: string;
  icon: string;
}

const enum QueryMenuId {
  Save = 1,
  Delete = 2
}

// LineSettings contained within TelemetryNode tree
interface LineSettings {
  lineColor: string; // rgb color
  yAxisPosition: Y_AXIS_POSITION; // none, left, right
  minYaxis: number | null,
  maxYaxis: number | null,
  showTitle: boolean;
  function: FUNC_TYPE;
  functionTimeframe: number;
}

interface LineSubscription {
  sourceId: string
  deviceId: string
  lineId: string
  displayName: string
  ltmi: string
  settings: LineSettings | undefined
  bulkData: Array<any>
  data: Array<any>
}

@Component({
  selector: 'ems-analyze',
  templateUrl: './analyze.component.html',
  styleUrls: ['./analyze.component.scss']
})
export class AnalyzeComponent implements OnInit {
  private unsubscribe: Subject<void> = new Subject();
  private currentTransactionId: string | null = null;
  private lineWidgetDatasets: Array<any> = [];
  private chart: Chart | undefined;
  private chartOptionSettings: any = {};
  private lineSubscriptions: Array<LineSubscription> = []
  private reloadRequired: boolean = false;
  private resetZoomDisabled: boolean = true;
  private windowWidth: number = window.innerWidth;
  private analyzeSettings: AnalyzeSettings | undefined = ANALYZE_DEFAULT;
  private loading: boolean = false;
  private worker: Worker | undefined;
  public queryList: Array<IQueryListItem> | undefined
  public currentQuery!: IQueryItem | undefined
  public selectedQueryId!: number | undefined
  private pendingQueryCreate: boolean = false

  public startDateControl = new FormControl(new Date(), this.validateRange());
  public endDateControl = new FormControl(new Date(), this.validateRange());

  // Initialize Elipse menus
  public elipseMenu: MenuItems[] = [
    { id: QueryMenuId.Save, label: 'global.button.save', icon: "save" },
    { id: QueryMenuId.Delete, label: 'global.button.delete', icon: 'delete' }
  ]

  constructor(
    private dialog: MatDialog,
    private getDataService: GetDataService,
    private definitionService: DefinitionService,
    private translationService: TranslationService,
    private toast: MatSnackBar,
    private dateAdapter: NgxMatDateAdapter<Date>,
    private queryService: QueryService,
    private spinner: NgxSpinnerService,
  ) {
    this.listenForQueriesLoaded()
    this.listenForDefinitionsLoaded()
    this.listenForGetDataUpdates()
    this.loadTranslations()
  } // constructor()

  private loadingId = 0;
  private loadingTimeout = MAX_TIMEOUT;

  ngOnInit(): void {
    fromEvent(window, 'resize').pipe(debounceTime(500))
      .subscribe(() => {
        this.windowWidth = window.innerWidth
      })

    // Set the current selected language for DAte Picker
    this.dateAdapter.setLocale(TranslationService.CurrentLanguage.CountryCode)

    // Set the starting Date/Time Range
    this.analyzeSettings = structuredClone(ANALYZE_DEFAULT)

    // Set default start/end Date/Time
    const endDateTime = new Date(Date.now() + (new Date().getTimezoneOffset() * (60 * 1000)))
    this.analyzeSettings.endDateTime = endDateTime.getTime()
    this.analyzeSettings.startDateTime = endDateTime.getTime() - DEFAULT_TIME_RANGE
    this.startDateControl.setValue(new Date(this.analyzeSettings.startDateTime))
    this.endDateControl.setValue(new Date(this.analyzeSettings.endDateTime))

    // Init the chart
    this.setChartOptions(this.analyzeSettings);

    // Create the chart
    const ctx = document.getElementById('analyzeChart');

    this.chart = new Chart(<ChartItem>ctx, {
      type: 'line',
      data: {
        datasets: this.lineWidgetDatasets
      },
      options: this.chartOptionSettings
    });

    if (this.queryService?.QueriesReady && DefinitionService.SourcesListReady) {
      this.loadQueries()
    } else {
      this.startSpinner()
    }
  } // ngOnInit()

  ngOnDestroy(): void {
    if (this.currentTransactionId) {
      this.getDataService.cancelGetData(this.currentTransactionId)
    }
    this.stopSpinner()
    window.clearInterval(this.loadingId)
    this.unsubscribe.next()
    this.unsubscribe.complete()
  } // ngOnDestroy()


  private listenForQueriesLoaded(): void {
    this.queryService.listenForQueryUpdates.pipe(takeUntil(this.unsubscribe)).subscribe(queryStatus => {
      // Is LTMIs and Queries ready?
      if (queryStatus.status === QueryRequestStatus.Reloaded && DefinitionService.SourcesListReady) {
        this.loadQueries()
        this.reloadRequired = false
        return
      }

      // Handle Query Create Request result
      if (this.pendingQueryCreate) {
        if (queryStatus?.status == QueryRequestStatus.Success) {
          this.queryList = [{
            id: -1,
            name: undefined,
            version: QUERY_VERSION
          }]
          this.queryList.push(...this.queryService.getQueryList())
          this.currentQuery = this.storageToQuery(<IQueryItem>this.queryService.getQueryById(queryStatus.data))
          this.selectedQueryId = this.currentQuery !== undefined ? this.currentQuery.id : undefined
          this.querySelectionChanged()
          this.reloadRequired = false
        } else {
          // Reload because something went wrong
          Logger.error("listenForQueriesLoaded unknown response")
          this.queryService.reloadQueries()
          this.startSpinner()
          return
        }
        this.pendingQueryCreate = false
        this.stopSpinner()
      }
    })
  } // listenForQueriesLoaded()

  private listenForDefinitionsLoaded(): void {
    this.definitionService.listenForSourceDefinitionStatus.pipe(takeUntil(this.unsubscribe)).subscribe(msg => {
      if (msg?.status === SourceDefinitionStatus.Success && this.queryService.QueriesReady) {
        this.loadQueries()
        this.reloadRequired = false
      }
    }); // subscribe()
  } // listenForDefinitionsLoaded()

  private startSpinner(): void {
    this.spinner.show(this.spinnerName)
    this.loadingTimeout = MAX_TIMEOUT
    this.loadingId = window.setInterval(() => {
      if (this.loadingTimeout && !--this.loadingTimeout) {
        this.spinner.hide(this.spinnerName)
        // Tell the user we timed out
        const msgBoxSettings: MessageBoxOptions = {
          type: MB_TYPE.YES_NO,
          title: undefined,
          message: "analyze.loading-timeout"
        }
        const msgBox = this.dialog.open(MessageBoxComponent, {
          data: msgBoxSettings
        });
        msgBox.afterClosed().subscribe((res) => {
          if (res?.event === MB_RESPONSE.YES) {
            this.loadingTimeout = MAX_TIMEOUT;
            this.spinner.show(this.spinnerName)
          } else {
            this.spinner.hide(this.spinnerName);
            window.clearInterval(this.loadingId)
            this.loadingId = 0;
          }
        }); // afterClosed subscribe
      }
      // Catch if system completed before user response
      if (this.queryService.QueriesReady && DefinitionService.SourcesListReady) {
        this.spinner.hide(this.spinnerName);
        window.clearInterval(this.loadingId)
        this.loadingId = 0;
      }
    }, MILL_SECONDS) // setInterval

  } // startSpinner()

  private stopSpinner(): void {
    this.spinner.hide(this.spinnerName);
    window.clearInterval(this.loadingId)
  }

  public get spinnerType(): string {
    return SpinnerTypes.BallClipRotate;
  }

  public get spinnerName(): string {
    return "queryList";
  }


  private buttonDeleteQuery(): void {
    // Not valid unless dashboard exists
    if (!this.selectedQueryId)
      return;

    // Confirm Delete
    const msgBoxSettings: MessageBoxOptions = {
      type: MB_TYPE.YES_NO,
      title: undefined,
      message: "analyze.delete-query"
    }
    const msgBox = this.dialog.open(MessageBoxComponent, {
      data: msgBoxSettings
    });
    msgBox.afterClosed().subscribe((res) => {
      if (res?.event === MB_RESPONSE.YES) {
        if (this.queryList && this.currentQuery) {
          const ndx = this.queryList.findIndex(item => item.id === this.currentQuery?.id)
          this.queryList.splice(ndx, 1);
          this.queryService.deleteQuery(this.currentQuery);
        }
        // Find first dashboard that isn't being deleted, if it exists
        this.selectedQueryId = undefined;
        this.currentQuery = undefined;
        this.reloadRequired = false
        this.querySelectionChanged()
      }
    });

  } // buttonDeleteQuery()


  private buttonSaveQuery(): void {

    let currentName: string = ""
    if (this.currentQuery) {
      currentName = this.currentQuery?.name ?? ""
    }

    const getNameDlg = this.dialog.open(QueryNameComponent, { data: currentName })
    getNameDlg.afterClosed().subscribe((res) => {

      if (res.event != RESPONSE.Save)
        return

      /// If the name is same, then update
      if (this.currentQuery && currentName == res.data) {
        this.currentQuery.name = res.data
        this.currentQuery.version = QUERY_VERSION
        this.currentQuery.settings = this.analyzeSettings
        this.queryService?.saveQuery(this.queryToStorage(structuredClone(this.currentQuery)))
        this.chart?.update()
      } else { // Create new query
        this.currentQuery = {
          id: 0,
          name: res.data,
          version: QUERY_VERSION,
          settings: this.analyzeSettings
        }
        this.pendingQueryCreate = true
        this.queryService.createQuery(this.queryToStorage(structuredClone(this.currentQuery)))
        this.startSpinner()
      } // if/else
    }) // getNameDlg.afterClosed()

  } // buttonSaveQuery()


  private loadQueries(): void {
    // Insert an empty item
    this.queryList = [{
      id: -1,
      name: undefined,
      version: QUERY_VERSION
    }]

    this.queryList.push(...this.queryService.getQueryList())

    this.currentQuery = {
      id: -1,
      name: undefined,
      version: QUERY_VERSION,
      settings: ANALYZE_DEFAULT
    }

    this.analyzeSettings = ANALYZE_DEFAULT
    this.selectedQueryId = -1
    this.analyzeSettings = this.currentQuery.settings
    this.reloadRequired = false

    this.querySelectionChanged()
    this.stopSpinner()
  } // loadQueries()

  private storageToQuery(query: IQueryItem): IQueryItem {
    if (query.settings && query.settings.sourceNodes) {
      query.settings.sourceNodes = this.storageToTree(query.settings.sourceNodes, DefinitionService.getSourcesList())
    }

    return query
  } // storageToQuery()

  private queryToStorage(query: IQueryItem): IQueryItem {
    if (query.settings) {
      query.settings.sourceNodes = this.treeToStorage(query.settings.sourceNodes)
    }
    return query
  } // queryToStorage()

  public storageToTree(stored: IStorageTreeNode[], full: ITreeNode[]): ITreeNode[] {
    if (!stored.length || !full.length)
      return []

    const result: ITreeNode[] = []

    stored.forEach((node) => {
      // see if clean node has this node
      const found = full.find((element) => node.name == element.name)

      if (NodeTypes.getNodeType(found) === TreeNodeType.Device) {
        (<IDeviceNode>found).children = DefinitionService.getDeviceChildrenFromLTMI(
          (<IDeviceNode>found).ltmi, undefined, undefined, undefined, (<IDeviceNode>found).arrays, undefined)
      }

      if (node.children && (found && found.children)) {
        found.children = this.storageToTree(node.children, found.children);
        // Flag the parent
        found.indeterminate = found.children.some(child => child.selected || child.indeterminate);
        result.push(found);
      } else { // Possibly Leaf
        if (!node.children && found) {
          found.selected = true;
          (<ITelemetryNode>found).settings = (<ITelemetryNode>node).settings
          result.push(found);
          return result;
        } else { // Error/Mismatch
          // If Node is not found, then discard the node
          return result;
        } // if/else Leaf
      } // if/else (node.children/found.children)
      return result;
    }) // selected.forEach    return nodes;

    return result;
  } // storageToTree()

  public treeToStorage(nodes: ITreeNode[]): IStorageTreeNode[] {
    const result: IStorageTreeNode[] = [];

    nodes.forEach((node) => {
      const storageNode: IStorageTreeNode = {
        name: node.name
      };
      if ((<ITelemetryNode>node).settings)
        storageNode['settings'] = (<ITelemetryNode>node).settings
      if (node.children?.length)
        storageNode['children'] = node.children

      if (node.selected || node.indeterminate) {
        const tmp: IStorageTreeNode = {
          name: node.name
        };
        if ((<ITelemetryNode>node).settings)
          tmp['settings'] = (<ITelemetryNode>node).settings
        if (node.children?.length)
          tmp['children'] = node.children

        if (node.children != undefined && node.children.length > 0) {
          tmp.children = this.treeToStorage(node.children)
        }
        result.push(tmp)
      }
    });

    return result;
  } // treeToStorage()

  public querySelectionChanged() {

    if (this.currentTransactionId) {
      this.getDataService.cancelGetData(this.currentTransactionId)
      this.currentTransactionId = null
      this.loading = false
    }

    this.query.requests.splice(0)
    this.lineSubscriptions.splice(0)
    this.analyzeSettings = ANALYZE_DEFAULT
    this.reloadRequired = this.downloadDataAvailable = false
    this.currentQuery = undefined

    let found = undefined
    if (this.queryList && this.selectedQueryId) {
      found = <IQueryItem>this.queryList.find((query) => query.id === this.selectedQueryId)
    }
    // If default selected, then reset chart
    if (found && found.id != -1) {
      // Query Selected, so update
      this.currentQuery = this.storageToQuery(<IQueryItem>this.queryService.getQueryById(found.id))
      this.selectedQueryId = this.currentQuery?.id
      this.analyzeSettings = this.currentQuery?.settings
      if (this.analyzeSettings?.sourceNodes?.length) {
        this.buildSubscriptionRequest(<any>this.analyzeSettings.sourceNodes)
        this.reloadRequired = true
      }
    } else {
      this.analyzeSettings.sourceNodes = []
      this.currentQuery = undefined
      this.reloadRequired = false
    }

    // Set default start/end Date/Time
    const endDateTime = new Date(Date.now() + (new Date().getTimezoneOffset() * (60 * 1000)))
    this.analyzeSettings!.endDateTime = endDateTime.getTime()
    this.analyzeSettings!.startDateTime = endDateTime.getTime() - DEFAULT_TIME_RANGE
    this.startDateControl.reset()
    this.endDateControl.reset()
    this.startDateControl.setValue(new Date(this.analyzeSettings!.startDateTime))
    this.endDateControl.setValue(new Date(this.analyzeSettings!.endDateTime))

    if (this.chart && this.analyzeSettings) {
      this.setChartOptions(this.analyzeSettings)
      this.configureLineWidget(this.lineSubscriptions);
      this.chart.data.datasets = this.lineWidgetDatasets
      this.chart.options = this.chartOptionSettings
      this.resetZoomClicked()
      this.chart.update()
    }

  } // querySelectionChanged()

  public get currentQueryName(): string {
    return this.currentQuery?.name ?? "";
  } // currentQueryName

  public isEditDeleteDisabled(id: number): boolean {
    if (id != QueryMenuId.Delete)
      return false

    return this.selectedQueryId == -1 || this.selectedQueryId == undefined
  } // isEditDeleteDisabled()

  public toolbarElipseClicked(selected: MenuItems): void {

    switch (selected.id) {
      case 1: // Save
        this.buttonSaveQuery()
        break
      case 2: // Delete
        this.buttonDeleteQuery()
        break
      default:
        Logger.error(`Uknown Analyze Elipse Selection ${selected}`)
    }
  }

  public get isDownloadAvailable(): boolean {
    return !this.downloadDataAvailable
  } // isDownloadAvailable()

  private createWorkerMessage(request: WorkerRequest, result: WorkerResult | undefined, data: any | undefined): WorkerMessage {
    return {
      request: request,
      result: result,
      data: data
    }
  } // createWorkerMessage()

  private downloadFilename: string = ""
  private onWorkerMessage(event: any): void {

    if (event.data.result !== WorkerResult.Success) {
      Logger.error(`Analyze onWorkerMessage failed: ${event.data}`)
    }

    // Setup the Download
    const docElement = document.createElement('a');
    const universalBOM = '\uFEFF';

    // You can edit the code for the file name according to your requirements
    const filename = this.downloadFilename + this.downloadFileIncrement + '.csv'
    docElement.href = 'data:text/csv;charset=utf-8,' + encodeURI(universalBOM + event.data.data);
    docElement.target = '_blank';
    docElement.download = filename;
    docElement.click();

    switch (event.data.request) {
      case WorkerResponse.NextPage:
        Logger.debug(`profileDownload NextPage ${(Date.now() - this.profileDownload) / MILL_SECONDS}`)
        this.downloadFileIncrement++
        break;
      case WorkerResponse.Complete:
        Logger.debug(`profileDownload Complete ${(Date.now() - this.profileDownload) / MILL_SECONDS}`)
        if (this.worker) {
          this.worker.terminate()
          this.worker = undefined
        }
        this.toast.open(this.labelToastCompleted, 'X', { horizontalPosition: 'end', verticalPosition: 'top' })
        break;
      default:
        Logger.error("Unknown Download Worker response")
    }
  } // onWorkerMessage()

  private onWorkerError(error: any): void {
    Logger.error(`onWorkerError: ${error}`)
  } // onWorkerError()

  private profileDownload: number = 0;

  private downloadFileIncrement: number = 1
  public downloadClicked(): void {
    this.downloadFileIncrement = 1

    if (!this.lineSubscriptions.length) {
      return
    }

    if (typeof Worker === 'undefined') {
      Logger.error("Web workers are not supported in this environment.")
      // TODO: Add Foreground processing with spinner or just terminate?
      // Web workers are not supported in this environment.
      return
    }

    // Terminate current download
    if (this.worker !== undefined) {
      this.worker.terminate()
    }

    this.downloadFilename = DOWNLOAD_FILENAME + new Date().toISOString() + "-";

    this.toast.open(this.labelToastStarted, 'X', { horizontalPosition: 'center', verticalPosition: 'top', duration: 5 * 1000 })

    this.worker = new Worker(new URL('./worker/download.worker', import.meta.url));
    this.worker.onmessage = (event) => this.onWorkerMessage(event);
    this.worker.onerror = (error) => this.onWorkerError(error);

    this.profileDownload = Date.now()

    this.worker.postMessage(this.createWorkerMessage(WorkerRequest.Download, WorkerResult.Success, this.lineSubscriptions))

  } // downloadClicked()

  public get isLoading(): boolean {
    return this.loading
  } // isLoading()

  public get resetZoomStatus(): boolean {
    return this.resetZoomDisabled
  } // resetZoomStatus()

  public isReloadable(): boolean {
    return (this.analyzeSettings && this.analyzeSettings.sourceNodes && this.analyzeSettings.sourceNodes.length > 0) && !this.isRangeValid() ? false : true;
  } // isReloadable()

  private isRangeValid(): boolean {
    if (!this.startDateControl || !this.endDateControl) {
      return false;
    }

    const startDate = new Date(<string>this.startDateControl.getRawValue()?.toISOString()).getTime()
    const endDate = new Date(<string>this.endDateControl.getRawValue()?.toISOString()).getTime()

    if (!startDate || !endDate) {
      return false;
    }

    return startDate > endDate ? true : false;
  } // isRangeValid()

  private validateRange(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      return this.isRangeValid() ? { valid: true } : null;
    }
  } // validateRange()


  private completedZoomPan(chart: any): void {
    this.resetZoomDisabled = false;
  } // zoomComplete()


  private setChartOptions(settings: AnalyzeSettings) {

    this.chartOptionSettings = {
      responsive: true,
      maintainAspectRatio: false,
      legend: {
        display: settings.showLegend,
        position: 'top'
      },
      plugins: {
        // decimation: {
        //   enabled: true,
        //   algorithm: 'lttb'
        // },
        title: {
          display: settings.title.showTitle,
          position: settings.title.position,
          align: settings.title.align,
          text: settings.title.text,
          font: {
            weight: settings.title.font.weight,
            size: settings.title.font.size
          }
        },
        zoom: {
          pan: {
            enabled: true,
            modifierKey: 'ctrl',
            onPanComplete: (chart: any) => this.completedZoomPan(chart),
          },
          zoom: {
            wheel: {
              enabled: true
            },
            pinch: {
              enabled: true
            },
            drag: {
              enabled: true,
              backgroundColor: 'rgba(0,0,0,0.2)',
              borderColor: 'rgba(0,0,0,0.5)',
              borderWidth: '1'
            },
            mode: 'xy',
            onZoomComplete: (chart: any) => this.completedZoomPan(chart),
          },
        },
      },
      interaction: {
        mode: 'nearest',
        axis: 'x',
        intersect: true
      },
      ticks: {
        source: 'auto',
        minRotation: 0,
        maxRotation: 0,
      },
      parsing: false,
      normalized: true,
      animation: false,
      spanGaps: true
    }

    this.chartOptionSettings['scales'] = {}
    this.chartOptionSettings['scales']['x'] = {
      type: 'time',
      time: {
        tooltipFormat: 'yyyy-MM-dd HH:mm:ss',
        displayFormats: {
          millisecond: "mm:ss.SSS",
          second: "HH:mm:ss",
          minute: "HH:mm",
          hour: "MM-dd HH:mm",
          day: 'MM-dd',
          month: "yyyy-MM"
        }
      },
      ticks: {
        source: 'auto',
        maxTicksLimit: 20
      },
      min: settings.startDateTime,
      max: settings.endDateTime
    }
  } // setChartOptions()

  private configureLineWidget(subscriptions: Array<LineSubscription>) {
    // Initiliaze
    this.lineWidgetDatasets.splice(0);
    // For each of the subscriptions
    subscriptions.forEach((line, pos) => {
      if (line.settings !== undefined) {
        // Set the ChartDataset for each line
        let key = `${line.sourceId}#${line.deviceId}#${line.lineId}`
        this.lineWidgetDatasets.push(structuredClone({
          label: line.displayName,
          backgroundColor: line.settings.lineColor,
          borderColor: line.settings.lineColor,
          cubicInterpolationMode: 'monotone',
          fill: false,
          // tension: 0.5,
          yAxisID: key,
          pointHitRadius: 20,
          pointHoverRadius: 20,
          pointRadius: 0, // Hides the point, default 3
          pointBorderColor: line.settings.lineColor,
          pointBackgroundColor: line.settings.lineColor,
          data: []
        })) // push lineWidgetDataset

        // Setup the Line Y Axis
        this.chartOptionSettings['scales'][key] = {
          title: {
            display: line.settings.showTitle,
            text: line.displayName,
            color: line.settings.lineColor,
            font: {
              weight: 'bolder'
            }
          },
          type: 'linear',
          display: (line.settings.yAxisPosition == Y_AXIS_POSITION.NONE) ? false : true,
          position: (line.settings.yAxisPosition == Y_AXIS_POSITION.LEFT) ? Y_AXIS_POSITION.LEFT : Y_AXIS_POSITION.RIGHT,
          min: line.settings.minYaxis,
          max: line.settings.maxYaxis
        } // Setup the Line Y Axis
      } // If Line.Settings valid
    }) // forEach Subscription
  } // configureLineWidget()

  private downloadDataAvailable: boolean = false
  private listenForGetDataUpdates(): void {
    // Listen for the GetData Service events
    this.getDataService.getDataUpdated.pipe(takeUntil(this.unsubscribe)).subscribe((message) => {
      if (message.transactionId !== this.currentTransactionId)
        return;

      // Process my transaction
      switch (message.status) {
        case GetDataRequestStatus.NextPage:
          Logger.debug(`NextPage ${(Date.now() - this.profileStartTime) / MILL_SECONDS}`)
          this.processGetData(message.data)
          break;
        case GetDataRequestStatus.Complete:
          this.currentTransactionId = null
          this.loading = false
          this.downloadDataAvailable = true
          Logger.debug(`Complete ${(Date.now() - this.profileStartTime) / MILL_SECONDS}`)
          this.processGetData(message.data)
          break;
        case GetDataRequestStatus.Failed:
          this.currentTransactionId = null
          Logger.error(`GetData Failed: ${message}`)
          this.loading = false
          this.reloadRequired = false
          break;
      } // switch(message.status)
    });

  } // listenForGetDataUpdates()

  private processGetData(message: any): void {
    try {
      // Used to work around ChartJS moving data to local time (timezone: didn't work)
      const offsetInMilliseconds = new Date().getTimezoneOffset() * (60 * 1000)
      for (let i = 0; i < message.length; i++) {
        const item = message[i];
        for (let x = 0; x < this.lineSubscriptions.length; x++) {
          const line = this.lineSubscriptions[x];
          const arrayIndex = line.lineId.indexOf("]")
          if (arrayIndex > 0 && arrayIndex < line.lineId.length-1) {
            const lineId = line.lineId.substring(line.lineId.lastIndexOf("]", line.lineId.length - 2) + 1)
            const key = line.sourceId + '#' + line.deviceId + '#' + line.lineId.substring(0, line.lineId.lastIndexOf("]", line.lineId.length - 2) + 1)
            if ((item[lineId] && item[lineId].N != null)
              && (item[KeyWord.telemetryDDBprimaryKey].S === key)
            ) {
              line.bulkData.push({ x: (Number(item[KeyWord.telemetryDDBsortKey].N) + offsetInMilliseconds), y: Number(item[lineId].N) });
            }
          } else {
            if ((item[line.lineId] && item[line.lineId].N != null)
              && (item[KeyWord.telemetryDDBprimaryKey].S === (line.sourceId + "#" + line.deviceId))
            ) {
              line.bulkData.push({ x: (Number(item[KeyWord.telemetryDDBsortKey].N) + offsetInMilliseconds), y: Number(item[line.lineId].N) });
            }
          }

        }
      }
    } catch (error) {
      Logger.error(`processGetData: ${error}`)
    }

    this.mergeBulkData();
  } // processGetData()


  public startDateChanged() {
    if (this.analyzeSettings) {
      this.reloadRequired = true
    }
  } // startDateChanged()

  public endDateChanged() {
    if (this.analyzeSettings) {
      this.reloadRequired = true
    }
  } // endDateChanged()

  public reloadLabel(): string {
    return 'analyze.' + ((this.reloadRequired && this.analyzeSettings?.sourceNodes?.length) ? 'reload-required' : 'reload')
  } // reloadLabel()

  // Change the Line Chart settings
  public settingsClicked(): void {

    const settingsDialog = this.dialog.open(AnalyzeSettingsComponent, { disableClose: true, data: { settings: this.analyzeSettings } });

    // Subscribe to the result
    settingsDialog.afterClosed().subscribe((res) => {
      if (res && res.event === 'apply') {
        this.analyzeSettings = structuredClone(res.data.settings);
        // Reload is already required, no point doing the update
        if (this.reloadRequired || this.analyzeSettings == undefined) {
          return
        }

        const oldQuery = structuredClone(this.query)
        const oldSubscription = structuredClone(this.lineSubscriptions)
        this.query.requests.splice(0)
        this.lineSubscriptions.splice(0)

        this.buildSubscriptionRequest(<any>this.analyzeSettings.sourceNodes)

        // If signal list changed, then reload required
        if (JSON.stringify(oldQuery.requests) != JSON.stringify(this.query.requests)) {
          this.reloadRequired = true
          this.downloadDataAvailable = false
          return
        }

        this.setChartOptions(this.analyzeSettings)
        this.configureLineWidget(this.lineSubscriptions);
        // Copy the BulkData back
        this.lineSubscriptions.forEach(line => {
          const old = oldSubscription.find(old => {
            return (old.sourceId == line.sourceId && old.deviceId == line.deviceId && old.lineId == line.lineId)
          })
          if (old) {
            line.bulkData = structuredClone(old.bulkData)
          }
        })
        this.mergeBulkData()

        if (this.chart) {
          this.chart.data.datasets = this.lineWidgetDatasets
          this.chart.options = this.chartOptionSettings
          this.chart.update()
        }
      }
    });
  } // settingsClicked()

  private arraySignalKey: Array<string> = [];
  private sourceId!: string;
  private sourceDisplayName!: string;
  private deviceId!: string;
  private deviceDisplayName!: string;
  private query: QueryRequest = { requests: [], startTime: 0, endTime: 0, sort: SORT_ORDER.NONE };
  private buildSubscriptionRequest(nodes: Array<any>): void {

    nodes.forEach((node) => {
      switch (NodeTypes.getNodeType(node)) {
        case TreeNodeType.Source:
          this.arraySignalKey = []
          this.sourceId = node.name;
          this.sourceDisplayName = node.displayName

          if (node.children) { // If no, then mis-configured tree
            this.buildSubscriptionRequest(node.children);
          }
          break;
        case TreeNodeType.ArrayNode:
          if (node.children) {
            this.buildSubscriptionRequest(node.children);
          }
          break;
        case TreeNodeType.ObjectNode:
          this.arraySignalKey.push(node.name)
          if (node.children) {
            this.buildSubscriptionRequest(node.children);
            this.arraySignalKey.pop()
          }
          break;
        case TreeNodeType.Group:
          if (node.children) {
            this.buildSubscriptionRequest(node.children);
          }
          break
        case TreeNodeType.Device:
          this.arraySignalKey = []
          this.deviceId = node.name
          this.deviceDisplayName = node.displayName

          if (node.children) {
            this.buildSubscriptionRequest(node.children);
          }
          break;
        case TreeNodeType.Telemetry:
          const key = (this.arraySignalKey.length ? (this.arraySignalKey.join("")) : '') + node.name
          this.appendQueryRequest(this.sourceId + "#" + this.deviceId, key)

          let displayName = ""
          if (node.settings.showSourceName) {
            displayName = this.sourceDisplayName + "->"
          }
          if (node.settings.showDeviceName) {
            displayName += this.deviceDisplayName + "->"
          }
          
          displayName += node.settings.showCustomName ? node.settings.customName : node.displayName

          if (node.settings.showUoMSymbol) {
            const uomSymbol = DefinitionService.getUoMSymbol(node.ltmiRef.semanticType, node.ltmiRef.unit)
            if (uomSymbol) {
              displayName += "(" + uomSymbol + ")"
            }
          }

          const subscription: LineSubscription = {
            sourceId: this.sourceId,
            deviceId: this.deviceId,
            displayName: displayName,
            ltmi: node.ltmi,
            lineId: key,
            settings: node.settings,
            bulkData: [],
            data: []
          }
          this.lineSubscriptions.push(subscription)
          break;
        default: // Something is wrong
          Logger.error("AnalyzeSettings-getSubscriptions() - unknown type: ", node)
          break;
      }
    });
  } // buildSubscriptionRequest()

  private appendQueryRequest(primaryKey: string, signal: string) {
    // If this contains arrays
    const arrayIndex = signal.lastIndexOf("]")
    if (arrayIndex >= 0) {
      // Is this an Array of signals?
      if (arrayIndex == signal.length - 1) {
        const subIndex = signal.lastIndexOf("]", arrayIndex - 1)
        // Is this an array of?
        if (subIndex > 0) {
          primaryKey = primaryKey + '#' + signal.substring(0, subIndex + 1)
          signal = signal.substring(subIndex + 1)
        }
      } else { // Not SubArrays
        primaryKey = primaryKey + '#' + signal.substring(0, arrayIndex + 1)
        signal = signal.substring(arrayIndex + 1)
      }
    }

    // If Existing Query, Update
    let found = this.query.requests.find(request => (request.key === primaryKey))
    if (found) {
      found.signals.push(signal)
      return
    }

    // New Query
    this.query.requests.push({
      key: primaryKey,
      signals: [signal]
    })
  } // appendQueryRequest()

  private mergeBulkData(): void {
    // Iterate through the subscriptions
    // For each lineSubscription
    if (this.lineSubscriptions === undefined) {
      Logger.error("LineSubs Undefined")
      return;
    }
    for (let linePos = 0; linePos < this.lineSubscriptions.length; linePos++) {
      const line = this.lineSubscriptions[linePos];

      if (!line.bulkData.length)
        continue;

      line.bulkData.sort((a: any, b: any) => a.x > b.x ? 1 : -1);

      switch (line.settings?.function) {
        case FUNC_TYPE.MINMAX:
          let tmpMinMax = [];
          for (let i = 0; i < line.bulkData.length;) {
            let min: any = { x: null, y: null };
            let max: any = { x: null, y: null };
            let cnt = 0;
            const nextEndTime = line.bulkData[i].x + (line.settings!.functionTimeframe * MILL_SECONDS);
            do {
              const item = line.bulkData[i + cnt];
              if (min.x == null || min.y > item.y) {
                min.y = item.y;
                min.x = item.x;
              }
              if (max.x == null || max.y < item.y) {
                max.y = item.y;
                max.x = item.x;
              }
              cnt++;
            } while (((i + cnt) < line.bulkData.length) && (nextEndTime >= line.bulkData[i + cnt].x))
            i += cnt;
            if (max.x) {
              tmpMinMax.push({ x: max.x, y: max.y });
            }
            if (min.x) {
              tmpMinMax.push({ x: min.x, y: min.y });
            }
          }
          tmpMinMax.sort((a: any, b: any) => a.x > b.x ? 1 : -1);
          line.data.push(...tmpMinMax);
          break;
        case FUNC_TYPE.AVERAGE:
          for (let i = 0; i < line.bulkData.length;) {
            let sum = 0;
            let cnt = 0;
            let lastTimestamp = 0;
            const nextEndTime = line.bulkData[i].x + (line.settings!.functionTimeframe * MILL_SECONDS);
            do {
              lastTimestamp = line.bulkData[i + cnt].x;
              sum += line.bulkData[i + cnt++].y;
            } while (((i + cnt) < line.bulkData.length) && (nextEndTime >= line.bulkData[i + cnt].x));
            i += cnt;
            if (cnt > 0) {
              line.data.push({ x: lastTimestamp, y: (sum / cnt) });
            }
          }
          break;
        case FUNC_TYPE.REALTIME:
        default:
          if (line.bulkData.length) {
            line.data = line.data.concat(line.bulkData)
          }
          break;
      } // Switch

      line.data.sort((a: any, b: any) => a.x > b.x ? 1 : -1)
      // Use this if ChartJS Decimation Plugin used
      this.lineWidgetDatasets[linePos].data = Decimation.largestTriangleThreeBuckets(line.data, this.windowWidth, "x", "y")
      if (this.chart) {
        this.chart.update()
      }
    } // Iterate through the subscriptions
  } // mergeBulkData()

  public resetZoomClicked(): void {
    if (this.chart) {
      this.chart.resetZoom()
    }
    this.resetZoomDisabled = true
  } // resetZoom()

  private profileStartTime: number = 0;

  public reloadClicked(): void {
    this.resetZoomDisabled = true
    this.reloadRequired = false
    this.downloadDataAvailable = false

    if (!this.chart || !this.analyzeSettings) {
      return
    }
    // If current Loading inprogress
    if (this.currentTransactionId) {
      this.getDataService.cancelGetData(this.currentTransactionId)
      this.currentTransactionId = null
    }
    // Init the chart
    this.setChartOptions(this.analyzeSettings);

    this.lineSubscriptions.splice(0);
    this.query.requests.splice(0);

    this.buildSubscriptionRequest(<any>this.analyzeSettings.sourceNodes)

    // Build the query and adjust the chart settings
    const tzOffset = new Date().getTimezoneOffset() * (60 * 1000)
    this.query.startTime = new Date(<string>this.startDateControl.getRawValue()?.toISOString()).getTime() - tzOffset
    this.analyzeSettings.startDateTime = new Date(<string>this.startDateControl.getRawValue()?.toISOString()).getTime()
    this.query.endTime = new Date(<string>this.endDateControl.getRawValue()?.toISOString()).getTime() - tzOffset
    this.analyzeSettings.endDateTime = new Date(<string>this.endDateControl.getRawValue()?.toISOString()).getTime()

    if (this.chart && this.analyzeSettings) {
      this.setChartOptions(this.analyzeSettings)
      this.configureLineWidget(this.lineSubscriptions);
      this.chart.data.datasets = this.lineWidgetDatasets
      this.chart.options = this.chartOptionSettings
      this.resetZoomClicked()
      this.chart.update()
    }

    this.profileStartTime = Date.now();
    this.currentTransactionId = this.getDataService.getData(this.query);
    this.loading = true;
    if (this.currentTransactionId == null) {
      Logger.error(`Failed getData Request ${JSON.stringify(this.query)}`)
    }
  } // reloadClicked()

  // Translation code
  private labelToastStarted: string = "";
  private labelToastCompleted: string = "";
  private loadTranslations(): void {
    const label_toastStarted: string = 'analyze.toast-download-started';
    const label_toastCompleted: string = 'analyze.toast-download-completed';
    const labels: Array<string> = [
      label_toastStarted, label_toastCompleted
    ];

    this.translationService.get(labels)
      .pipe(
        takeUntil(this.unsubscribe)
      ).subscribe(translations => {
        this.labelToastStarted = translations[label_toastStarted];
        this.labelToastCompleted = translations[label_toastCompleted];
      });
  } // loadTranslations()

} // AnalyzeComponent()
